/* oxlint-disable no-param-reassign */
import type { IGenericListPropertiesMissing } from '../../../../../../../../types/global';
import textAlignItems from '../../text-align-items';
import type { IEnv } from '../../../../../types/types';
import frequencies from '../../../constants';
import updateForFrequencyMax from '../update-frequency-max';
import autoSortCriterias from '../sort-criterias';
import type { INxAppLayout } from '../../../../../hooks/types/index.d';
import isDirectQueryEnabled from '../../../../../hooks/direct-query/is-direct-query-enabled';
import { getExpressionConvertFunctions } from './attribute-expression-util';

const DEFAULT_LAYOUT_ORDER = 'column';

const change = updateForFrequencyMax;

export default function getPresentation(env: IEnv) {
  const { translator } = env;
  const { isEnabled } = env?.flags || {};
  const imagesEnabled = !!isEnabled?.('TLV_1879_IMAGES_FILTERPANE');
  const readOnly = (_properties: unknown, _handler: unknown, args: { app: { layout: INxAppLayout } }) =>
    isDirectQueryEnabled({ appLayout: args?.app?.layout });

  // Shared "max visible columns/rows" controls, reused by both the text grid and image grid.

  type ShowFn = (data: EngineAPI.IGenericListProperties & IGenericListPropertiesMissing) => boolean;
  const autoCustomOptions = [
    { value: true, label: translator.get('Common.Auto'), tooltipTranslation: 'Common.Auto' },
    { value: false, label: translator.get('Common.Custom'), tooltipTranslation: 'Common.Custom' },
  ];
  const pixelOptions = (maxPx: number) => Array.from({ length: maxPx + 1 }, (_, i) => ({ value: i, label: `${i}px` }));
  const maxVisibleColumnsAutoItem = (show: ShowFn) => ({
    type: 'boolean',
    component: 'switch',
    ref: 'layoutOptions.maxVisibleColumns.auto',
    translation: 'properties.filterpane.maxVisibleColumnsAuto',
    defaultValue: true,
    options: autoCustomOptions,
    show,
  });
  const maxVisibleColumnsItem = (show: ShowFn, defaultValue = 3) => ({
    type: 'number',
    ref: 'layoutOptions.maxVisibleColumns.maxColumns',
    translation: 'properties.filterpane.gridModeMaxColumns',
    defaultValue,
    show,
  });
  const maxVisibleRowsAutoItem = (show: ShowFn) => ({
    type: 'boolean',
    component: 'switch',
    ref: 'layoutOptions.maxVisibleRows.auto',
    translation: 'properties.filterpane.maxVisibleRowsAuto',
    defaultValue: true,
    options: autoCustomOptions,
    show,
  });
  const maxVisibleRowsItem = (show: ShowFn, defaultValue = 3) => ({
    type: 'number',
    ref: 'layoutOptions.maxVisibleRows.maxRows',
    translation: 'properties.filterpane.gridModeMaxRows',
    defaultValue,
    show,
  });
  const isImageRepresentation: ShowFn = (data) => data.representation?.type === 'image';

  const imageRepresentationItems = imagesEnabled
    ? {
        representation: {
          type: 'items',
          ref: 'representation.type',
          grouped: true,
          show: true,
          items: {
            type: {
              ref: 'representation.type',
              type: 'string',
              component: 'dropdown',
              translation: 'Object.Table.Representation',
              defaultValue: 'text',
              options: [
                {
                  value: 'text',
                  translation: 'Object.Table.Representation.Text',
                },
                {
                  value: 'image',
                  translation: 'Object.Table.Representation.Image',
                },
              ],
              change(itemData: EngineAPI.IGenericListProperties & IGenericListPropertiesMissing) {
                itemData.layoutOptions = itemData.layoutOptions || {};
                if (itemData.representation?.type === 'image') {
                  itemData.layoutOptions.dataLayout = 'grid';
                  itemData.layoutOptions.layoutOrder = 'row';
                  itemData.layoutOptions.maxVisibleColumns = {
                    auto: true,
                    maxColumns: 5,
                  };
                  itemData.layoutOptions.maxVisibleRows = {
                    auto: true,
                    maxRows: 4,
                  };
                  itemData.representation.imageSize = itemData.representation.imageSize ?? 'alwaysFit';
                  // Ensure state-first sorting when switching to image, especially if showSelected is enabled
                  if (itemData.representation?.showSelected) {
                    itemData.qListObjectDef.qDef.qSortCriterias = itemData.qListObjectDef.qDef.qSortCriterias.map(
                      (criteria) => ({ ...criteria, qSortByState: 1 }),
                    );
                  }
                  return;
                }

                itemData.layoutOptions.dataLayout = 'singleColumn';
                itemData.layoutOptions.layoutOrder = 'row';
                itemData.layoutOptions.maxVisibleColumns = {
                  auto: true,
                  maxColumns: 3,
                };
                itemData.layoutOptions.maxVisibleRows = {
                  auto: true,
                  maxRows: 3,
                };
                itemData.qListObjectDef.qDef.autoSort = true;
                for (let i = 0; i < itemData.qListObjectDef.qDef.qSortCriterias.length; i++) {
                  itemData.qListObjectDef.qDef.qSortCriterias[i] = autoSortCriterias;
                }
              },
            },
            representationImage: {
              type: 'items',
              grouped: true,
              show(data: EngineAPI.IGenericListProperties & IGenericListPropertiesMissing) {
                return data.representation?.type === 'image';
              },
              items: {
                imageSource: {
                  type: 'items',
                  items: {
                    imageSetting: {
                      ref: 'representation.imageSetting',
                      translation: 'properties.filterpane.image.setting',
                      type: 'string',
                      component: 'dropdown',
                      defaultValue: 'label',
                      options: [
                        {
                          value: 'label',
                          translation: 'properties.filterpane.image.dimValue',
                        },
                        {
                          value: 'url',
                          translation: 'properties.filterpane.image.urlExpr',
                        },
                      ],
                      show(data: EngineAPI.IGenericListProperties & IGenericListPropertiesMissing) {
                        return data.representation?.type === 'image';
                      },
                    },
                    imageLabel: {
                      component: 'expression',
                      type: 'string',
                      expressionType: 'measure',
                      translation: 'Object.Table.Representation.Image.ImageLabel',
                      show(data: EngineAPI.IGenericListProperties & IGenericListPropertiesMissing) {
                        const { representation } = data ?? {};
                        return (
                          representation?.type === 'image' && (representation?.imageSetting ?? 'label') === 'label'
                        );
                      },
                      convertFunctions: getExpressionConvertFunctions('imageLabel'),
                    },
                    imageUrl: {
                      component: 'expression',
                      type: 'string',
                      expressionType: 'measure',
                      translation: 'Object.Table.Representation.Image.ImageUrl',
                      show(data: EngineAPI.IGenericListProperties & IGenericListPropertiesMissing) {
                        const { representation } = data ?? {};
                        return representation?.type === 'image' && representation?.imageSetting === 'url';
                      },
                      convertFunctions: getExpressionConvertFunctions('imageUrl'),
                    },
                    subtitle: {
                      component: 'expression',
                      type: 'string',
                      expressionType: 'measure',
                      translation: 'properties.filterpane.image.subtitle',
                      show(data: EngineAPI.IGenericListProperties & IGenericListPropertiesMissing) {
                        const repr = data.representation;
                        return repr?.type === 'image' && repr?.textOverlay !== false;
                      },
                      convertFunctions: getExpressionConvertFunctions('subtitle'),
                    },
                    tooltip: {
                      component: 'expression',
                      type: 'string',
                      expressionType: 'measure',
                      translation: 'properties.filterpane.image.tooltip',
                      show(data: EngineAPI.IGenericListProperties & IGenericListPropertiesMissing) {
                        return data.representation?.type === 'image';
                      },
                      convertFunctions: getExpressionConvertFunctions('tooltip'),
                    },
                    showSelected: {
                      ref: 'representation.showSelected',
                      translation: 'properties.filterpane.image.showSelected',
                      type: 'boolean',
                      component: 'switch',
                      defaultValue: false,
                      options: [
                        { value: true, translation: 'properties.on' },
                        { value: false, translation: 'properties.off' },
                      ],
                      show(data: EngineAPI.IGenericListProperties & IGenericListPropertiesMissing) {
                        return data.representation?.type === 'image';
                      },
                      // The image grid only renders selected values it can find sorted to the top
                      // of the list (state-first), so force it on without disturbing the rest of
                      // sort criteria
                      change(data: EngineAPI.IGenericListProperties & IGenericListPropertiesMissing) {
                        if (data.representation?.showSelected) {
                          data.qListObjectDef.qDef.qSortCriterias.forEach((criteria) => {
                            criteria.qSortByState = 1;
                          });
                        }
                      },
                    },
                    sortByStateMessage: {
                      component: 'text',
                      translation: 'properties.filterpane.image.showSelected.sortByStateMessage',
                      show(data: EngineAPI.IGenericListProperties & IGenericListPropertiesMissing) {
                        return data.representation?.type === 'image';
                      },
                    },
                  },
                },
                imageSizing: {
                  type: 'items',
                  grouped: false,
                  show: true,
                  items: {
                    imageSize: {
                      ref: 'representation.imageSize',
                      translation: 'Object.Table.Representation.Image.Size',
                      type: 'string',
                      component: 'dropdown',
                      defaultValue: 'alwaysFit',
                      options: [
                        {
                          value: 'alwaysFit',
                          translation: 'properties.backgroundImage.sizeAlwaysFit',
                        },
                        {
                          value: 'fitWidth',
                          translation: 'properties.backgroundImage.sizeFitWidth',
                        },
                        {
                          value: 'fitHeight',
                          translation: 'properties.backgroundImage.sizeFitHeight',
                        },
                        {
                          value: 'stretch',
                          translation: 'properties.backgroundImage.sizeStretch',
                        },
                        {
                          value: 'alwaysFill',
                          translation: 'properties.backgroundImage.sizeAlwaysFill',
                        },
                      ],
                      show(data: EngineAPI.IGenericListProperties & IGenericListPropertiesMissing) {
                        return data.representation?.type === 'image';
                      },
                    },
                    imagePosition: {
                      ref: 'representation.imagePosition',
                      label: translator.get('Common.Position'),
                      component: 'position-grid',
                      defaultValue: 'top-center',
                      show(data: EngineAPI.IGenericListProperties & IGenericListPropertiesMissing) {
                        return data.representation?.imageSize !== 'stretch' && data.representation?.type === 'image';
                      },
                      currentSizeRef: 'representation.imageSize',
                    },
                    maxVisibleColumnsAuto: maxVisibleColumnsAutoItem(isImageRepresentation),
                    maxVisibleColumns: maxVisibleColumnsItem((data) => {
                      const { maxVisibleColumns } = data.layoutOptions || {};
                      const { auto = true } = maxVisibleColumns || {};
                      return isImageRepresentation(data) && auto !== true;
                    }, 5),
                    maxVisibleRowsAuto: maxVisibleRowsAutoItem(isImageRepresentation),
                    maxVisibleRows: maxVisibleRowsItem((data) => {
                      const { maxVisibleRows } = data.layoutOptions || {};
                      const { auto = true } = maxVisibleRows || {};
                      return isImageRepresentation(data) && auto !== true;
                    }, 4),
                    gridGap: {
                      ref: 'representation.gridGap',
                      translation: 'properties.filterpane.image.gridGap',
                      type: 'number',
                      min: 0,
                      max: 100,
                      defaultValue: 1,
                      show(data: EngineAPI.IGenericListProperties & IGenericListPropertiesMissing) {
                        return data.representation?.type === 'image';
                      },
                    },
                  },
                },
                textGroup: {
                  type: 'items',
                  grouped: false,
                  show(data: EngineAPI.IGenericListProperties & IGenericListPropertiesMissing) {
                    return data.representation?.type === 'image';
                  },
                  items: {
                    showText: {
                      ref: 'representation.textOverlay',
                      translation: 'properties.filterpane.image.textOverlay',
                      type: 'boolean',
                      component: 'switch',
                      defaultValue: true,
                      options: [
                        { value: true, translation: 'properties.on' },
                        { value: false, translation: 'properties.off' },
                      ],
                      show(data: EngineAPI.IGenericListProperties & IGenericListPropertiesMissing) {
                        return data.representation?.type === 'image';
                      },
                    },
                    titlePosition: {
                      ref: 'representation.titlePosition',
                      translation: 'properties.filterpane.image.title.position',
                      component: 'position-grid',
                      defaultValue: 'top-center',
                      show(data: EngineAPI.IGenericListProperties & IGenericListPropertiesMissing) {
                        const repr = data.representation;
                        return repr?.type === 'image' && repr?.textOverlay !== false;
                      },
                    },
                    titleBackground: {
                      ref: 'representation.titleBackground',
                      translation: 'properties.filterpane.image.textBackground',
                      type: 'boolean',
                      component: 'switch',
                      defaultValue: true,
                      options: [
                        { value: true, translation: 'properties.on' },
                        { value: false, translation: 'properties.off' },
                      ],
                      show(data: EngineAPI.IGenericListProperties & IGenericListPropertiesMissing) {
                        const repr = data.representation;
                        return repr?.type === 'image' && repr?.textOverlay !== false;
                      },
                    },
                  },
                },
                cellBackgroundColor: {
                  type: 'items',
                  grouped: false,
                  show: true,
                  items: {
                    cellBgColorMode: {
                      ref: 'representation.cellBgColorMode',
                      translation: 'properties.filterpane.image.cellBackgroundColor',
                      type: 'string',
                      component: 'dropdown',
                      defaultValue: 'single',
                      options: [
                        { value: 'single', translation: 'properties.colorMode.primary' },
                        { value: 'expression', translation: 'properties.colorMode.byExpression' },
                        { value: 'selectionState', translation: 'properties.styling.selectionState' },
                      ],
                      show(data: EngineAPI.IGenericListProperties & IGenericListPropertiesMissing) {
                        return data.representation?.type === 'image';
                      },
                    },
                    cellBgColorSingle: {
                      ref: 'representation.cellBgColor',
                      type: 'object',
                      translation: 'properties.filterpane.image.backgroundColor',
                      component: 'color-picker',
                      width: false,
                      showAlpha: true,
                      show(data: EngineAPI.IGenericListProperties & IGenericListPropertiesMissing) {
                        const repr = data.representation;
                        return repr?.type === 'image' && (repr?.cellBgColorMode ?? 'single') === 'single';
                      },
                    },
                    cellBgColorExpr: {
                      component: 'expression',
                      type: 'string',
                      expressionType: 'measure',
                      show(data: EngineAPI.IGenericListProperties & IGenericListPropertiesMissing) {
                        return (
                          data.representation?.type === 'image' && data.representation?.cellBgColorMode === 'expression'
                        );
                      },
                      convertFunctions: getExpressionConvertFunctions('cellBgColor'),
                    },
                  },
                },
                outline: {
                  type: 'items',
                  grouped: false,
                  show: true,
                  items: {
                    borderWidth: {
                      ref: 'representation.borderWidth',
                      translation: 'properties.Outline',
                      type: 'integer',
                      component: 'dropdown',
                      defaultValue: 0,
                      options: pixelOptions(10),
                      show(data: EngineAPI.IGenericListProperties & IGenericListPropertiesMissing) {
                        return data.representation?.type === 'image';
                      },
                    },
                    borderColorMode: {
                      ref: 'representation.borderColorMode',
                      translation: 'properties.filterpane.image.borderColor',
                      type: 'string',
                      component: 'dropdown',
                      defaultValue: 'single',
                      options: [
                        { value: 'single', translation: 'properties.colorMode.primary' },
                        { value: 'expression', translation: 'properties.colorMode.byExpression' },
                        { value: 'selectionState', translation: 'properties.styling.selectionState' },
                      ],
                      show(data: EngineAPI.IGenericListProperties & IGenericListPropertiesMissing) {
                        const repr = data.representation;
                        return repr?.type === 'image' && (repr?.borderWidth ?? 0) > 0;
                      },
                    },
                    borderColor: {
                      ref: 'representation.borderColor',
                      type: 'object',
                      translation: 'properties.filterpane.image.borderColor',
                      component: 'color-picker',
                      width: false,
                      showAlpha: true,
                      show(data: EngineAPI.IGenericListProperties & IGenericListPropertiesMissing) {
                        const repr = data.representation;
                        return (
                          repr?.type === 'image' &&
                          (repr?.borderWidth ?? 0) > 0 &&
                          (repr?.borderColorMode ?? 'single') === 'single'
                        );
                      },
                    },
                    borderColorExpr: {
                      component: 'expression',
                      type: 'string',
                      expressionType: 'measure',
                      show(data: EngineAPI.IGenericListProperties & IGenericListPropertiesMissing) {
                        return (
                          data.representation?.type === 'image' &&
                          (data.representation?.borderWidth ?? 0) > 0 &&
                          data.representation?.borderColorMode === 'expression'
                        );
                      },
                      convertFunctions: getExpressionConvertFunctions('borderColor'),
                    },
                    cornerRadius: {
                      ref: 'representation.cornerRadius',
                      translation: 'properties.filterpane.image.cornerRadius',
                      type: 'integer',
                      component: 'dropdown',
                      defaultValue: 4,
                      options: pixelOptions(24),
                      show(data: EngineAPI.IGenericListProperties & IGenericListPropertiesMissing) {
                        return data.representation?.type === 'image';
                      },
                    },
                  },
                },
              },
            },
          },
        },
      }
    : {};

  // The options (text alignment, dense mode, checkboxes, histogram, frequency count, collapse mode).
  // They don't apply to the image grid, so they are shown only when the representation is 'text'.

  const showForText = (data: EngineAPI.IGenericListProperties & IGenericListPropertiesMissing) =>
    data.representation?.type !== 'image';

  const textItems = {
    textAlignGroup: {
      ...textAlignItems.textAlignGroup,
    },
    denseMode: {
      ref: 'layoutOptions.dense',
      component: 'checkbox',
      translation: 'properties.filterpane.showDense',
      defaultValue: false,
    },
    checkboxes: {
      ref: 'checkboxes',
      translation: 'properties.filterpane.showCheckboxes',
      component: 'checkbox',
      defaultValue: false,
      readOnly,
    },
    histogram: {
      ref: 'histogram',
      grouped: true,
      component: 'checkbox',
      translation: 'properties.filterpane.showHistogram',
      defaultValue: false,
      change,
      readOnly,
    },
    frequencyCountModeGroup: {
      translation: 'properties.frequencyCountMode',
      type: 'items',
      grouped: true,
      items: {
        frequencyCountMode: {
          ref: 'qListObjectDef.qFrequencyMode',
          type: 'string',
          component: 'dropdown',
          defaultValue: frequencies.FREQUENCY_NONE,
          show(_properties: unknown, _handler: unknown, args: { app: { layout: INxAppLayout } }) {
            const isDQ = isDirectQueryEnabled({ appLayout: args?.app?.layout });
            return !isDQ && isEnabled?.('LIST_BOX_FREQUENCY_COUNT');
          },
          translation: 'properties.frequencyCountMode',
          options: [
            {
              value: frequencies.FREQUENCY_NONE,
              translation: 'properties.frequencyCountNone',
            },
            {
              value: frequencies.FREQUENCY_VALUE,
              translation: 'properties.frequencyCountAsValue',
            },
            {
              value: frequencies.FREQUENCY_PERCENT,
              translation: 'properties.frequencyCountAsPercent',
            },
          ],
          change,
          convertFunctions: {
            get(
              getter: () => EngineAPI.FrequencyModeType,
              definition: unknown,
              args: unknown,
              itemData: EngineAPI.IGenericListProperties & IGenericListPropertiesMissing,
            ) {
              if (itemData.qListObjectDef.frequencyEnabled) {
                return getter();
              }
              return frequencies.FREQUENCY_NONE;
            },
            set(
              value: EngineAPI.FrequencyModeType,
              setter: (type: string, newValue: EngineAPI.FrequencyModeType) => void,
              definition: unknown,
              args: unknown,
              itemData: EngineAPI.IGenericListProperties & IGenericListPropertiesMissing,
            ) {
              if (value !== frequencies.FREQUENCY_NONE) {
                setter('string', value);
                itemData.qListObjectDef.frequencyEnabled = true;
              } else if (itemData.histogram) {
                itemData.qListObjectDef.frequencyEnabled = false;
                setter('string', frequencies.FREQUENCY_VALUE);
              } else {
                itemData.qListObjectDef.frequencyEnabled = false;
                setter('string', frequencies.FREQUENCY_NONE);
              }
            },
          },
        },
      },
    },
    collapseModeGroup: {
      type: 'items',
      grouped: true,
      items: {
        collapseMode: {
          ref: 'layoutOptions.collapseMode',
          type: 'string',
          component: 'dropdown',
          translation: 'properties.filterpane.collapseMode',
          defaultValue: 'auto',
          options: [
            {
              value: 'auto',
              translation: 'Common.Auto',
            },
            {
              value: 'always',
              translation: 'Common.Always',
            },
            {
              value: 'never',
              translation: 'Common.Never',
            },
          ],
        },
      },
    },
    gridModeGroup: {
      grouped: true,
      type: 'items',
      items: {
        gridMode: {
          type: 'string',
          ref: 'layoutOptions.dataLayout',
          translation: 'properties.filterpane.layoutModeLabel',
          component: 'buttongroup',
          defaultValue: 'singleColumn',
          options: [
            {
              value: 'singleColumn',
              label: translator.get('properties.filterpane.singleColumn'),
              tooltipTranslation: 'properties.filterpane.singleColumn',
            },
            {
              value: 'grid',
              label: translator.get('properties.filterpane.grid'),
              tooltipTranslation: 'properties.filterpane.grid',
            },
          ],
          change(itemData: EngineAPI.IGenericListProperties & IGenericListPropertiesMissing) {
            // Make sure layoutOrder is not undefined when we are in grid mode.
            if (itemData.layoutOptions.dataLayout === 'grid') {
              itemData.layoutOptions.layoutOrder = itemData.layoutOptions.layoutOrder ?? DEFAULT_LAYOUT_ORDER;
            }
          },
        },
        layoutOrder: {
          type: 'string',
          component: 'buttongroup',
          ref: 'layoutOptions.layoutOrder',
          translation: 'properties.filterpane.layoutOrder',
          defaultValue: DEFAULT_LAYOUT_ORDER,
          options: [
            {
              value: 'row',
              label: translator.get('properties.filterpane.layoutOrderRow'),
              tooltipTranslation: 'properties.filterpane.layoutOrderRow',
            },
            {
              value: 'column',
              label: translator.get('properties.filterpane.layoutOrderColumn'),
              tooltipTranslation: 'properties.filterpane.layoutOrderColumn',
            },
          ],
          show(data: EngineAPI.IGenericListProperties) {
            const { dataLayout } = data.layoutOptions || {};
            return dataLayout === 'grid';
          },
        },
        maxVisibleRowsAuto: maxVisibleRowsAutoItem((data) => {
          const { dataLayout, layoutOrder = DEFAULT_LAYOUT_ORDER } = data.layoutOptions || {};
          return dataLayout === 'grid' && layoutOrder === 'column';
        }),
        maxVisibleRows: maxVisibleRowsItem((data) => {
          const { dataLayout, layoutOrder = DEFAULT_LAYOUT_ORDER, maxVisibleRows } = data.layoutOptions || {};
          const { auto = true } = maxVisibleRows || {};
          return dataLayout === 'grid' && layoutOrder === 'column' && auto !== true;
        }),
        maxVisibleColumnsAuto: maxVisibleColumnsAutoItem((data) => {
          const { dataLayout, layoutOrder } = data.layoutOptions || {};
          return dataLayout === 'grid' && layoutOrder === 'row';
        }),
        maxVisibleColumns: maxVisibleColumnsItem((data) => {
          const { dataLayout, layoutOrder, maxVisibleColumns } = data.layoutOptions || {};
          const { auto = true } = maxVisibleColumns || {};
          return dataLayout === 'grid' && layoutOrder === 'row' && auto !== true;
        }),
      },
    },
  };

  const items = imagesEnabled
    ? {
        ...imageRepresentationItems,
        textRepresentation: { type: 'items', show: showForText, items: textItems },
      }
    : textItems;

  return {
    type: 'items',
    translation: 'properties.presentation',
    items,
  };
}
